// src/components/operationCenter/widgets/PortfolioPulseWidget.tsx
// Portfolio Pulse - Clean overview with NAV, P&L, sparkline, health metrics, and top movers

import React, { useMemo, useState, useEffect, useRef } from "react";
import * as echarts from "echarts";
import type { ECharts, EChartsOption } from "echarts";
import type { WidgetProps } from "@/types/dashboard";
import { usePortfolioNav, usePortfolioCurrent, useAlertEvents, usePortfolioHoldings } from "@/hooks/portfolio";
import { useTokensChanges } from "@/hooks/tokens/compare/useCompareData";
import { useAccessToken } from "@/hooks/useAccessToken";
import { TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import type { NavPoint } from "@/types/portfolio";

type TimeRange = "24h" | "7d" | "30d";

interface PortfolioPulseConfig {
  time_range?: TimeRange;
}

const PortfolioPulseWidget: React.FC<WidgetProps<PortfolioPulseConfig>> = ({ widget, onConfigChange }) => {
  const config = widget.config as PortfolioPulseConfig;
  const [timeRange, setTimeRange] = useState<TimeRange>(config.time_range ?? "7d");
  const authToken = useAccessToken();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);

  // Fetch data
  const { data: navData, isLoading: navLoading } = usePortfolioNav(authToken);
  const { data: currentData, isLoading: currentLoading } = usePortfolioCurrent(authToken);
  const { data: alertsData, isLoading: alertsLoading } = useAlertEvents(authToken, 50);
  const { data: holdingsData, isLoading: holdingsLoading } = usePortfolioHoldings(authToken);
  const { changeByTicker, query: changesQuery } = useTokensChanges();

  const isLoading = navLoading || currentLoading || alertsLoading || holdingsLoading || changesQuery.isLoading;
  const current = useMemo(() => currentData?.data ?? {}, [currentData?.data]);
  const holdings = useMemo(() => holdingsData?.data ?? [], [holdingsData?.data]);

  // Process NAV data for sparkline
  const navSeries = useMemo(() => {
    const series: NavPoint[] = navData?.data?.performance_data ?? [];
    if (series.length === 0) return { dates: [], values: [], currentValue: 0, change: 0, changePercent: 0 };

    const days = timeRange === "24h" ? 1 : timeRange === "7d" ? 7 : 30;
    const lastDate = series[series.length - 1]?.date;
    const cutoff = new Date(new Date(lastDate + "T00:00:00Z").getTime() - days * 24 * 60 * 60 * 1000);
    const filtered = series.filter(p => new Date(p.date + "T00:00:00Z") >= cutoff);

    const currentValue = filtered[filtered.length - 1]?.portfolio_value ?? 0;
    const startValue = filtered[0]?.portfolio_value ?? currentValue;
    const change = currentValue - startValue;
    const changePercent = startValue > 0 ? (change / startValue) * 100 : 0;

    return {
      dates: filtered.map(p => p.date),
      values: filtered.map(p => p.portfolio_value),
      currentValue,
      change,
      changePercent,
    };
  }, [navData, timeRange]);

  // Health metrics
  const healthMetrics = useMemo(() => {
    const vol30d = current['vol_30d'] ?? 0;
    const maxDD = current['max_drawdown_90d'] ?? 0;
    const hhi = current['hhi'] ?? 0;
    const sharpe = current['sharpe_30d'] ?? 0;

    return [
      { label: "Vol", value: `${(vol30d * 100).toFixed(1)}%` },
      { label: "DD", value: `${(Math.abs(maxDD) * 100).toFixed(1)}%` },
      { label: "HHI", value: hhi.toFixed(2) },
      { label: "Sharpe", value: sharpe.toFixed(2) },
    ];
  }, [current]);

  // Alert count
  const alertCount = useMemo(() => {
    const events = alertsData?.data ?? [];
    return events.filter(e => e.status !== 'closed').length;
  }, [alertsData]);

  // Top movers from portfolio holdings
  const topMovers = useMemo(() => {
    const changeKey = timeRange === "24h" ? "price_close_1d" : timeRange === "7d" ? "price_close_7d" : "price_close_30d";

    const withChanges = holdings
      .map(h => {
        const changes = changeByTicker.get(h.ticker);
        const change = changes?.[changeKey] as number | null | undefined;
        return {
          ticker: h.ticker,
          change: typeof change === 'number' && Number.isFinite(change) ? change : null,
        };
      })
      .filter(h => h.change !== null) as { ticker: string; change: number }[];

    const sorted = [...withChanges].sort((a, b) => b.change - a.change);

    return {
      gainers: sorted.slice(0, 2),
      losers: sorted.slice(-2).reverse(),
    };
  }, [holdings, changeByTicker, timeRange]);

  // Sparkline chart
  useEffect(() => {
    const container = chartRef.current;
    if (!container || navSeries.values.length === 0 || isLoading) return;

    // Dispose existing chart before creating new one
    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }

    // Small delay to ensure container is rendered
    const initTimer = setTimeout(() => {
      const chart = echarts.init(container);
      chartInstanceRef.current = chart;

      const isPositive = navSeries.changePercent >= 0;
      const color = isPositive ? "#10b981" : "#ef4444";

      const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: { top: 2, right: 2, bottom: 2, left: 2 },
        xAxis: { type: "category", data: navSeries.dates, show: false },
        yAxis: { type: "value", show: false },
        series: [{
          type: "line",
          data: navSeries.values,
          smooth: true,
          symbol: "none",
          lineStyle: { color, width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: isPositive ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.25)" },
                { offset: 1, color: isPositive ? "rgba(16, 185, 129, 0.02)" : "rgba(239, 68, 68, 0.02)" },
              ],
            },
          },
        }],
      };

      chart.setOption(option);
      chart.resize();
    }, 100);

    const handleResize = () => chartInstanceRef.current?.resize();
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [navSeries, isLoading]);

  useEffect(() => {
    return () => { chartInstanceRef.current?.dispose(); };
  }, []);

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    onConfigChange?.({ time_range: range });
  };

  const formatCurrency = (n: number) => {
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center px-3 py-2 border-b border-slate-700/30">
          <div className="animate-pulse h-3 w-24 bg-slate-700/50 rounded" />
        </div>
        <div className="flex-1 p-3">
          <div className="animate-pulse space-y-3">
            <div className="h-6 w-28 bg-slate-700/50 rounded" />
            <div className="h-20 bg-slate-700/30 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const isPositive = navSeries.changePercent >= 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {widget.title ?? "Portfolio Pulse"}
        </span>
        <div className="flex items-center gap-1">
          {(["24h", "7d", "30d"] as TimeRange[]).map(r => (
            <button
              key={r}
              onClick={() => handleTimeRangeChange(r)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                timeRange === r
                  ? "bg-indigo-500/25 text-indigo-300"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-3 min-h-0">
        {/* NAV Value Section */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">Portfolio Value</div>
            <div className="text-2xl font-bold text-white leading-tight">
              {formatCurrency(navSeries.currentValue)}
            </div>
          </div>
          <div className="text-right">
            <div className={`flex items-center justify-end gap-1 text-sm font-semibold ${
              isPositive ? "text-emerald-400" : "text-red-400"
            }`}>
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {isPositive ? "+" : ""}{navSeries.changePercent.toFixed(2)}%
            </div>
            <div className={`text-xs ${isPositive ? "text-emerald-400/70" : "text-red-400/70"}`}>
              {isPositive ? "+" : ""}{formatCurrency(navSeries.change)}
            </div>
          </div>
        </div>

        {/* Sparkline */}
        <div ref={chartRef} style={{ height: 50, width: '100%' }} />

        {/* Health Metrics Bar */}
        <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-slate-700/30">
          {healthMetrics.map((m, idx) => (
            <div key={m.label} className={`flex-1 text-center ${idx < healthMetrics.length - 1 ? 'border-r border-slate-700/30' : ''}`}>
              <div className="text-[9px] text-slate-500 uppercase">{m.label}</div>
              <div className="text-[11px] font-semibold text-slate-200">{m.value}</div>
            </div>
          ))}
          {alertCount > 0 && (
            <div className="flex-1 text-center">
              <div className="text-[9px] text-slate-500 uppercase">Alerts</div>
              <div className="flex items-center justify-center gap-0.5 text-[11px] font-semibold text-red-400">
                <AlertCircle className="w-3 h-3" />
                {alertCount}
              </div>
            </div>
          )}
        </div>

        {/* Top Movers */}
        {(topMovers.gainers.length > 0 || topMovers.losers.length > 0) && (
          <div className="flex gap-4 pt-2 mt-2 border-t border-slate-700/30">
            {/* Gainers */}
            <div className="flex-1">
              <div className="text-[9px] text-slate-500 uppercase mb-1">Top Gainers</div>
              <div className="space-y-0.5">
                {topMovers.gainers.map(g => (
                  <div key={g.ticker} className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-300 font-medium">{g.ticker}</span>
                    <span className={`text-[10px] font-semibold ${g.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {g.change >= 0 ? '+' : ''}{g.change.toFixed(1)}%
                    </span>
                  </div>
                ))}
                {topMovers.gainers.length === 0 && (
                  <span className="text-[10px] text-slate-500">-</span>
                )}
              </div>
            </div>
            {/* Losers */}
            <div className="flex-1">
              <div className="text-[9px] text-slate-500 uppercase mb-1">Top Losers</div>
              <div className="space-y-0.5">
                {topMovers.losers.map(l => (
                  <div key={l.ticker} className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-300 font-medium">{l.ticker}</span>
                    <span className={`text-[10px] font-semibold ${l.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {l.change >= 0 ? '+' : ''}{l.change.toFixed(1)}%
                    </span>
                  </div>
                ))}
                {topMovers.losers.length === 0 && (
                  <span className="text-[10px] text-slate-500">-</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioPulseWidget;
